/**
 * CustomCampoTelefone — seletor de país (bandeira SVG + DDI) via dropdown + input com
 * máscara, no estilo do tema (input-group + form-floating). Guarda em E.164 (+55…),
 * padrão BR. Bandeiras em SVG (flag-icons) porque emoji de bandeira não renderiza
 * no Windows. A bandeira define o DDI; o número usa máscara BR quando o país é BR.
 *
 * O país fica em estado INTERNO: um E.164 vazio não consegue codificar "país X sem
 * número", então guardar só no value faria a seleção voltar pra BR quando o número
 * está vazio.
 */
import { useEffect, useState } from 'react';
import { Dropdown, InputGroup } from 'react-bootstrap';
import 'flag-icons/css/flag-icons.min.css';

const PAISES = [
  { code: 'br', nome: 'Brasil', ddi: '55' },
  { code: 'us', nome: 'EUA', ddi: '1' },
  { code: 'pt', nome: 'Portugal', ddi: '351' },
  { code: 'ar', nome: 'Argentina', ddi: '54' },
  { code: 'uy', nome: 'Uruguai', ddi: '598' },
  { code: 'es', nome: 'Espanha', ddi: '34' },
  { code: 'it', nome: 'Itália', ddi: '39' },
  { code: 'fr', nome: 'França', ddi: '33' },
  { code: 'gb', nome: 'Reino Unido', ddi: '44' },
  { code: 'de', nome: 'Alemanha', ddi: '49' },
];

// máscara BR: (00) 0000-0000 (fixo) ou (00) 00000-0000 (celular)
function mascaraBR(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : '';
  const cel = d.length > 10;
  const p1 = d.slice(0, 2);
  const p2 = d.slice(2, cel ? 7 : 6);
  const p3 = d.slice(cel ? 7 : 6);
  return `(${p1}) ${p2}${p3 ? `-${p3}` : ''}`;
}

// separa o DDI (país) do número, a partir de um E.164 "+55...".
function parse(value: string): { code: string; numero: string } {
  const d = (value ?? '').replace(/\D/g, '');
  if (!d) return { code: 'br', numero: '' };
  const ordenados = [...PAISES].sort((a, b) => b.ddi.length - a.ddi.length);
  const p = ordenados.find((x) => d.startsWith(x.ddi));
  if (p) return { code: p.code, numero: d.slice(p.ddi.length) };
  return { code: 'br', numero: d };
}

export function CustomCampoTelefone({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  // País em estado interno (a fonte da verdade da bandeira).
  const [code, setCode] = useState(() => parse(value).code);

  // Quando o value externo chega com um DDI reconhecível (ex.: Puxar do ERP),
  // sincroniza a bandeira. Não mexe se o value estiver vazio (mantém a seleção manual).
  useEffect(() => {
    if ((value ?? '').replace(/\D/g, '')) setCode(parse(value).code);
  }, [value]);

  const pais = PAISES.find((p) => p.code === code) ?? PAISES[0];
  const p = parse(value);
  const numero = p.code === code ? p.numero : '';
  const numeroFmt = pais.code === 'br' ? mascaraBR(numero) : numero;

  const trocarPais = (novoCode: string) => {
    setCode(novoCode);
    const alvo = PAISES.find((x) => x.code === novoCode) ?? PAISES[0];
    const dig = numero.replace(/\D/g, '');
    onChange(dig ? `+${alvo.ddi}${dig}` : '');
  };

  const digitar = (v: string) => {
    const dig = v.replace(/\D/g, '');
    onChange(dig ? `+${pais.ddi}${dig}` : '');
  };

  return (
    <InputGroup>
      <Dropdown>
        <Dropdown.Toggle
          variant="outline-secondary"
          className="d-flex align-items-center gap-1"
          style={{ height: 'calc(3.5rem + 2px)' }}
          title="País / DDI"
        >
          <span className={`fi fi-${pais.code}`}></span>
          <span className="small">+{pais.ddi}</span>
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ maxHeight: 280, overflowY: 'auto' }}>
          {PAISES.map((x) => (
            <Dropdown.Item
              key={x.code}
              active={x.code === code}
              onClick={() => trocarPais(x.code)}
            >
              <span className={`fi fi-${x.code} me-2`}></span>
              {x.nome} <span className="text-secondary">+{x.ddi}</span>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
      <div className="form-floating flex-grow-1">
        <input
          id={id}
          type="tel"
          className="form-control"
          placeholder={label}
          value={numeroFmt}
          onChange={(e) => digitar(e.target.value)}
        />
        <label htmlFor={id}>{label}</label>
      </div>
    </InputGroup>
  );
}
